import React, { useCallback, useState, useEffect } from "react";
import CommonActionMenu from "../../components/common/CommonActionMenu";
import MainLayout from "../../components/layout/MainLayout";
import PageLoader from "../../components/common/PageLoader";
import CommonTable from "../../components/common/CommonTable";
import api from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission } from "../../utils/permissions";
import Swal from "sweetalert2";
import "./Deductions.css";

const getTodayDateString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = `${today.getMonth() + 1}`.padStart(2, "0");
  const day = `${today.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getFinancialYearEndDate = (financialYear) => {
  if (!financialYear) {
    return getTodayDateString();
  }

  const [startYear] = String(financialYear).split("-");
  const endYear = Number(startYear) + 1;
  return `${endYear}-03-31`;
};

const FINANCIAL_YEAR_MONTHS = [
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
  "January",
  "February",
  "March",
];

const createPriorEmploymentMonths = (rows = []) => {
  let normalizedRows = rows;

  if (typeof normalizedRows === "string") {
    try {
      normalizedRows = JSON.parse(normalizedRows);
    } catch {
      normalizedRows = [];
    }
  }

  if (!Array.isArray(normalizedRows)) {
    normalizedRows = [];
  }

  const rowsByMonth = new Map(
    normalizedRows
      .filter((row) => row && typeof row === "object" && row.month)
      .map((row) => [row.month, row]),
  );

  return FINANCIAL_YEAR_MONTHS.map((month) => ({
    month,
    gross_salary: rowsByMonth.get(month)?.gross_salary ?? "",
    tds_deducted: rowsByMonth.get(month)?.tds_deducted ?? "",
  }));
};

const formatPriorCurrency = (amount) =>
  `₹${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(Number(amount || 0))}`;

function DeductionsList() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [archiveEmployees, setArchiveEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [currentFinancialYear, setCurrentFinancialYear] = useState("");
  const [financialYears, setFinancialYears] = useState([]);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [selectedArchiveFY, setSelectedArchiveFY] = useState("");
  const [currentAsOfDate, setCurrentAsOfDate] = useState(getTodayDateString());
  const [selectedArchiveDate, setSelectedArchiveDate] = useState("");

  // ── Investments & Healthcare table state ─────────────────────────
  const [showDeductionModal, setShowDeductionModal] = useState(false);

  // ── HRA table state ───────────────────────────────────────────────
  const [showHRAModal, setShowHRAModal] = useState(false);

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [deductionForms, setDeductionForms] = useState([]);
  const [activeJanMar, setActiveJanMar] = useState(0);

  // 🔥 NEW PRIOR STATE
  const [showPriorModal, setShowPriorModal] = useState(false);
  const [priorData, setPriorData] = useState([]);
  const [priorAllData, setPriorAllData] = useState([]);
  const [archivePriorAllData, setArchivePriorAllData] = useState([]);
  const canManageTaxDeclarations = hasPermission(user, "tax_declarations.manage");


  const fetchData = useCallback(async (
    financialYear = "",
    asOfDate = getTodayDateString(),
  ) => {
    try {
      const [investmentsRes, formsRes] = await Promise.all([
        api.get("/employee-investments", {
          params: {
            ...(financialYear ? { financial_year: financialYear } : {}),
            ...(asOfDate ? { as_of_date: asOfDate } : {}),
          },
        }),
        api.get("/investment-forms"),
      ]);
      if (investmentsRes.data.success) {
        setEmployees(investmentsRes.data.data);
        const meta = investmentsRes.data.meta || {};
        setCurrentFinancialYear(
          meta.current_financial_year || financialYear || currentFinancialYear,
        );
        setFinancialYears(meta.available_financial_years || []);
      }
      if (formsRes.data.success) {
        setDeductionForms(formsRes.data.data.deductions);
        setActiveJanMar(formsRes.data.data.active_jan_mar);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load deduction data",
      });
    } finally {
      setLoading(false);
    }
  }, [currentFinancialYear]);

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return `₹${new Intl.NumberFormat("en-IN").format(amount)}`;
  };

  // ── Filtered lists ────────────────────────────────────────────────

  const employeeBaseColumns = [
    {
      header: "Sr. No.",
      render: (_, index) => index + 1,
    },
    {
      header: "Emp. ID",
      key: "code",
    },
    {
      header: "Name",
      render: (employee) => `${employee.first_name} ${employee.last_name}`,
    },
    {
      header: "Designation",
      render: (employee) =>
        employee.user_designation?.designation_detail?.designation_name ||
        "N/A",
    },
  ];
  // 🔥 FETCH ALL PRIOR DATA
  const fetchAllPrior = useCallback(async () => {
    const res = await api.get(`/all-prior-employment`);
    if (res.data.success) {
      setPriorAllData(res.data.data);
      const meta = res.data.meta || {};
      if (meta.available_financial_years?.length) {
        setFinancialYears(meta.available_financial_years);
      }
      if (meta.current_financial_year) {
        setCurrentFinancialYear(meta.current_financial_year);
      }
    }
  }, []);

  const fetchOwnPriorSummary = useCallback(async (financialYear = "") => {
    if (!user?.id) return;

    const res = await api.get(`/prior-employment/${user.id}`, {
      params: financialYear ? { financial_year: financialYear } : {},
    });

    if (res.data.success) {
      setPriorAllData(res.data.data || []);
      const meta = res.data.meta || {};
      if (meta.current_financial_year) {
        setCurrentFinancialYear(meta.current_financial_year);
      }
    }
  }, [user?.id]);
  // 🔥 FETCH PRIOR DATA
  const fetchPrior = async (userId, financialYear = "") => {
    const res = await api.get(`/prior-employment/${userId}`, {
      params: financialYear ? { financial_year: financialYear } : {},
    });
    if (res.data.success) {
      setPriorData(res.data.data);
    }
  };

  const fetchArchiveData = useCallback(async (financialYear) => {
    if (!financialYear) return;

    setArchiveLoading(true);
    try {
      const asOfDate = selectedArchiveDate || getFinancialYearEndDate(financialYear);
      const [investmentsRes, priorRes] = await Promise.all([
        api.get("/employee-investments", {
          params: { financial_year: financialYear, as_of_date: asOfDate },
        }),
        api.get("/all-prior-employment", {
          params: { financial_year: financialYear },
        }),
      ]);

      if (investmentsRes.data.success) {
        setArchiveEmployees(investmentsRes.data.data);
      }

      if (priorRes.data.success) {
        setArchivePriorAllData(priorRes.data.data);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load archive records",
      });
    } finally {
      setArchiveLoading(false);
    }
  }, [selectedArchiveDate]);


  useEffect(() => {
    const load = async () => {
      await fetchData("", currentAsOfDate);

      if (canManageTaxDeclarations) {
        await fetchAllPrior();
      } else {
        await fetchOwnPriorSummary(currentFinancialYear);
      }
    };

    load();
  }, [canManageTaxDeclarations, currentAsOfDate, currentFinancialYear, fetchAllPrior, fetchData, fetchOwnPriorSummary]);

  useEffect(() => {
    if (showArchiveModal && selectedArchiveFY) {
      fetchArchiveData(selectedArchiveFY);
    }
  }, [showArchiveModal, selectedArchiveFY, selectedArchiveDate, fetchArchiveData]);

  useEffect(() => {
    if (selectedArchiveFY) {
      setSelectedArchiveDate(getFinancialYearEndDate(selectedArchiveFY));
    }
  }, [selectedArchiveFY]);


  // 🔥 PRIOR TABLE
  const priorColumns = [
    ...employeeBaseColumns,
    {
      header: "Total Income Proccessed",
      render: (e) =>
      (
        formatCurrency(priorAllData.find((p) => p.user_id === e.id)?.gross_salary)
      )
    },
    {
      header: "Total TDS Deducted",
      render: (e) => (
        formatCurrency(priorAllData.find((p) => p.user_id === e.id)?.tds_deducted)
      )
    },
    {
      header: "Action",
      render: (employee) => (
        <CommonActionMenu
          actions={[
            {
              label: "Add / Edit",
              icon: "fas fa-plus",
              onClick: async () => {
                setSelectedEmployee(employee);
                await fetchPrior(employee.id, currentFinancialYear);
                setShowPriorModal(true);
              },
            },
          ]}
        />
      ),
    },
  ];

  const investmentColumns = [
    ...employeeBaseColumns,
    {
      header: "Amount",
      render: (employee) => formatCurrency(employee.total_inv),
    },
    {
      header: "Action",
      render: (employee) => (
        <CommonActionMenu
          ariaLabel={`Actions for ${employee.code}`}
          actions={[
            {
              label: "Edit Deduction",
              icon: "fas fa-edit",
              onClick: () => {
                setSelectedEmployee(employee);
                setShowDeductionModal(true);
              },
            },
          ]}
        />
      ),
    },
  ];
  const hraColumns = [
    ...employeeBaseColumns,
    {
      header: "Rent",
      render: (employee) => formatCurrency(employee.actual_rent_paid),
    },
    {
      header: "Action",
      render: (employee) => (
        <CommonActionMenu
          ariaLabel={`Actions for ${employee.code}`}
          actions={[
            {
              label: "Edit HRA",
              icon: "fas fa-edit",
              onClick: () => {
                setSelectedEmployee(employee);
                setShowHRAModal(true);
              },
            },
          ]}
        />
      ),
    },
  ];

  const archivePriorColumns = [
    ...employeeBaseColumns,
    {
      header: "Total Income Processed",
      render: (e) =>
        formatCurrency(
          archivePriorAllData.find((p) => p.user_id === e.id)?.gross_salary,
        ),
    },
    {
      header: "Total TDS Deducted",
      render: (e) =>
        formatCurrency(
          archivePriorAllData.find((p) => p.user_id === e.id)?.tds_deducted,
        ),
    },
  ];

  const archiveInvestmentColumns = [
    ...employeeBaseColumns,
    {
      header: "Amount",
      render: (employee) => formatCurrency(employee.total_inv),
    },
  ];

  const archiveHraColumns = [
    ...employeeBaseColumns,
    {
      header: "Rent",
      render: (employee) => formatCurrency(employee.actual_rent_paid),
    },
  ];

  if (loading) {
    return <MainLayout><PageLoader force copy="Loading deduction data..." /></MainLayout>;
  }

  return (
    <MainLayout>
      <div className="deductions-list-page">
        <div className="page-header">
          <div className="deductions-header-row">
            <div className="deductions-header-copy">
              <h4>Tax Declarations</h4>
              {currentFinancialYear && (
                <p className="text-muted mb-0">
                  Current financial year: <strong>{currentFinancialYear}</strong>
                </p>
              )}
              <p className="text-muted mb-0">
                Current view as of: <strong>{currentAsOfDate}</strong>
              </p>
            </div>
            <div className="deductions-header-actions">
              <input
                type="date"
                className="form-control deductions-date-input"
                value={currentAsOfDate}
                onChange={(e) => setCurrentAsOfDate(e.target.value)}
              />
              {canManageTaxDeclarations && financialYears.filter((fy) => fy !== currentFinancialYear).length >
                0 && (
                <button
                  className="btn btn-secondary deductions-archive-btn"
                  onClick={() => {
                    const firstArchiveYear = financialYears.find(
                      (fy) => fy !== currentFinancialYear,
                    );
                    setSelectedArchiveFY(firstArchiveYear || "");
                    setSelectedArchiveDate(
                      getFinancialYearEndDate(firstArchiveYear || ""),
                    );
                    setShowArchiveModal(true);
                  }}
                >
                  View FY Archive
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 🔥 PRIOR TABLE (TOP) */}
        <div className="table-card mb-4">

          <div className="table-card-header">
            <h6 className="table-card-title">
              <i className="fas fa-file-invoice-dollar me-2"></i>
              Prior Employment Financials
            </h6>
          </div>

          <CommonTable
            columns={priorColumns}
            data={employees}
            searchPlaceholder="Search..."
            tableClassName="deductions-table"
            wrapperClassName="table-responsive"
            noDataClassName="text-center"
            emptyMessage="No employees found"
            getSearchText={(employee) =>
              [
                employee.code,
                employee.first_name,
                employee.last_name,
                employee.user_designation?.designation_detail?.designation_name,
              ]
                .filter(Boolean)
                .join(" ")
            }
          />
        </div>

        {/* ── Table 1: Investments & Healthcare ───────────────── */}
        <div className="table-card mb-4">
          <div className="table-card-header">
            <h6 className="table-card-title">
              <i className="fas fa-file-invoice-dollar me-2"></i>
              Investments & Healthcare
            </h6>
          </div>

          <CommonTable
            columns={investmentColumns}
            data={employees}
            tableClassName="deductions-table"
            wrapperClassName="table-responsive"
            noDataClassName="text-center"
            emptyMessage="No employees found"
            searchPlaceholder="Search employees..."
            getSearchText={(employee) =>
              [
                employee.code,
                employee.first_name,
                employee.last_name,
                employee.user_designation?.designation_detail?.designation_name,
              ]
                .filter(Boolean)
                .join(" ")
            }
          />
        </div>

        {/* ── Table 2: HRA (Actual Rent Paid/Yr) ─────────────────── */}
        <div className="table-card">
          <div className="table-card-header">
            <h6 className="table-card-title">
              <i className="fas fa-home me-2"></i>
              Rent & Housing
            </h6>
          </div>

          <CommonTable
            columns={hraColumns}
            data={employees}
            tableClassName="deductions-table"
            wrapperClassName="table-responsive"
            noDataClassName="text-center"
            emptyMessage="No employees found"
            searchPlaceholder="Search employees..."
            getSearchText={(employee) =>
              [
                employee.code,
                employee.first_name,
                employee.last_name,
                employee.user_designation?.designation_detail?.designation_name,
              ]
                .filter(Boolean)
                .join(" ")
            }
          />
        </div>
      </div>

      {/* ── HRA Modal ─────────────────────────────────────────────── */}
      {showHRAModal && selectedEmployee && (
        <HRAModal
          employee={selectedEmployee}
          currentFinancialYear={currentFinancialYear}
          currentAsOfDate={currentAsOfDate}
          onClose={() => {
            setShowHRAModal(false);
            setSelectedEmployee(null);
            fetchData("", currentAsOfDate);
          }}
        />
      )}

      {/* ── Deduction Modal ───────────────────────────────────────── */}
      {showDeductionModal && selectedEmployee && (
        <DeductionModal
          employee={selectedEmployee}
          deductionForms={deductionForms}
          activeJanMar={activeJanMar}
          currentFinancialYear={currentFinancialYear}
          onClose={() => {
            setShowDeductionModal(false);
            setSelectedEmployee(null);
            fetchData("", currentAsOfDate);
          }}
        />
      )}
      {/* 🔥 PRIOR MODAL */}
      {showPriorModal && selectedEmployee && (
        <PriorModal
          employee={selectedEmployee}
          data={priorData}
          currentFinancialYear={currentFinancialYear}
          onClose={() => {
            setShowPriorModal(false);
            fetchData("", currentAsOfDate);
          }}
          onSaved={() =>
            canManageTaxDeclarations
              ? fetchAllPrior()
              : fetchOwnPriorSummary(currentFinancialYear)
          }
        />
      )}

      {canManageTaxDeclarations && showArchiveModal && (
        <ArchiveModal
          financialYears={financialYears.filter((fy) => fy !== currentFinancialYear)}
          selectedFinancialYear={selectedArchiveFY}
          onFinancialYearChange={setSelectedArchiveFY}
          selectedAsOfDate={selectedArchiveDate}
          onAsOfDateChange={setSelectedArchiveDate}
          employees={archiveEmployees}
          priorColumns={archivePriorColumns}
          tentativeColumns={archiveInvestmentColumns}
          hraColumns={archiveHraColumns}
          loading={archiveLoading}
          onClose={() => setShowArchiveModal(false)}
        />
      )}
    </MainLayout>
  );
}

// Deduction Modal Component
function DeductionModal({
  employee,
  deductionForms,
  activeJanMar,
  currentFinancialYear,
  onClose,
}) {
  const [deductions, setDeductions] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Load existing deductions
    const existingDeductions = {};

    if (employee.emp_investments) {
      employee.emp_investments.forEach((investment) => {
        if (investment.investment_type !== "Actual Rent Paid") {
          existingDeductions[investment.investment_type] = {
            value: investment.investment_value || "",
            status: "Saved",
          };
        }
      });
    }

    setDeductions(existingDeductions);
  }, [employee]);

  const handleDeductionChange = (name, value) => {
    setDeductions((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const investments = Object.entries(deductions)
        .filter(([key, value]) => value.value > 0)
        .map(([key, value]) => ({
          investment_type: key,
          investment_value: parseFloat(value.value),
        }));

      const response = await api.put(`/employee-investments/${employee.id}`, {
        request_type: "edit",
        financial_year: currentFinancialYear,
        investments: investments,
      });

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: response.data.message,
          timer: 2000,
          showConfirmButton: false,
        });
        onClose();
      }
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to update deductions";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const relevantDeductions = deductionForms.filter((ded) =>
    [
      "housing_loan",
      "under_80c",
      "under_80d",
      "under_80t",
      "under_80g",
      "under_80ccdb",
      "under_80a",
      "under_80a_ntr",
    ].includes(ded.name),
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content modal-large"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h5>
            Update Deduction - {employee.first_name} {employee.last_name}
          </h5>
          <button className="btn-close" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <p className="text-danger mb-3">
              <strong>All Data Must Be Entered Annually</strong>
            </p>

            {/* Header Row */}
            <div className="deduction-grid-header">
              <div></div>
              <div className="text-center">
                <strong>Amount (Apr-Dec)</strong>
              </div>
              {activeJanMar === 1 && (
                <div className="text-center">
                  <strong>Actual (Jan-Mar)</strong>
                </div>
              )}
            </div>

            {/* Deduction Rows */}
            {relevantDeductions.map((ded) => (
              <div key={ded.name} className="deduction-row-container">
                <div className="deduction-info">
                  <div className="deduction-label">{ded.label}</div>
                  <div className="deduction-limit">Upto: {ded.limit}</div>
                  <div className="deduction-status">
                    {deductions[ded.name]?.status || "Pending"}
                  </div>
                </div>

                {/* Amount Input */}
                <div className="deduction-input-group">
                  <input
                    type="number"
                    className="form-control"
                    placeholder="Enter amount"
                    value={deductions[ded.name]?.value || ""}
                    onChange={(e) =>
                      handleDeductionChange(ded.name, e.target.value)
                    }
                  />
                </div>

                {/* Actual Input (Jan-Mar) */}
                {activeJanMar === 1 && (
                  <div className="deduction-input-group">
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Enter amount"
                      value={deductions[`${ded.name}_ac`]?.value || ""}
                      onChange={(e) =>
                        handleDeductionChange(`${ded.name}_ac`, e.target.value)
                      }
                      disabled={ded.name === "housing_loan"}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={submitting}>
              {submitting ? "Updating..." : "Update"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── HRAModal ──────────────────────────────────────────────────────────
function HRAModal({ employee, currentFinancialYear, currentAsOfDate, onClose }) {
  const [formData, setFormData] = useState({
    financial_year: currentFinancialYear,
    actual_rent_paid: "",
    effective_from: currentAsOfDate || getTodayDateString(),
    effective_to: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const hraInvestment = employee.emp_investments?.find(
      (inv) => inv.investment_type === "Actual Rent Paid",
    );
    if (hraInvestment) {
      setFormData({
        financial_year: hraInvestment.financial_year || currentFinancialYear,
        actual_rent_paid: hraInvestment.investment_value || "",
        effective_from:
          hraInvestment.effective_from || currentAsOfDate || getTodayDateString(),
        effective_to: hraInvestment.effective_to || "",
      });
    } else {
      setFormData({
        financial_year: currentFinancialYear,
        actual_rent_paid: "",
        effective_from: currentAsOfDate || getTodayDateString(),
        effective_to: "",
      });
    }
  }, [employee, currentFinancialYear, currentAsOfDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.actual_rent_paid || formData.actual_rent_paid <= 0) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please enter a valid rent amount",
      });
      return;
    }

    if (!formData.effective_from) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please select the start date for this rent declaration",
      });
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post("/employee-investments", {
        user_id: employee.id,
        financial_year: formData.financial_year,
        investments: [
          {
            investment_type: "Actual Rent Paid",
            investment_value: parseFloat(formData.actual_rent_paid),
            effective_from: formData.effective_from,
            effective_to: formData.effective_to || null,
          },
        ],
      });

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: response.data.message,
          timer: 2000,
          showConfirmButton: false,
        });
        onClose();
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to update HRA",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h5>
            House Rent Allowance — {employee.first_name} {employee.last_name}
          </h5>
          <button className="btn-close" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label>
                Financial Year
              </label>
              <input className="form-control" value={formData.financial_year} disabled />
            </div>

            <div className="form-group">
              <label>
                Actual Rent Paid/Yr <span className="required">*</span>
              </label>
              <input
                type="number"
                className="form-control"
                placeholder="Enter annual rent amount"
                value={formData.actual_rent_paid}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    actual_rent_paid: e.target.value,
                  }))
                }
              />
            </div>

            <div className="form-group">
              <label>
                Effective From <span className="required">*</span>
              </label>
              <input
                type="date"
                className="form-control"
                value={formData.effective_from}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    effective_from: e.target.value,
                  }))
                }
              />
            </div>

            <div className="form-group">
              <label>Effective To</label>
              <input
                type="date"
                className="form-control"
                value={formData.effective_to}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    effective_to: e.target.value,
                  }))
                }
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={submitting}>
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// 🔥 PRIOR MODAL COMPONENT
function PriorModal({ employee, data, currentFinancialYear, onClose, onSaved }) {
  const currentRecord = Array.isArray(data) ? data[0] || {} : {};
  const [form, setForm] = useState({
    financial_year: currentRecord.financial_year || currentFinancialYear,
    monthly_breakdown: createPriorEmploymentMonths(currentRecord.monthly_breakdown),
  });

  useEffect(() => {
    const record = Array.isArray(data) ? data[0] || {} : {};
    setForm({
      financial_year: record.financial_year || currentFinancialYear,
      monthly_breakdown: createPriorEmploymentMonths(record.monthly_breakdown),
    });
  }, [data, currentFinancialYear]);

  const updateMonth = (index, field, value) => {
    setForm((current) => ({
      ...current,
      monthly_breakdown: current.monthly_breakdown.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row,
      ),
    }));
  };

  const totals = form.monthly_breakdown.reduce(
    (sum, row) => ({
      gross_salary: sum.gross_salary + Number(row.gross_salary || 0),
      tds_deducted: sum.tds_deducted + Number(row.tds_deducted || 0),
    }),
    { gross_salary: 0, tds_deducted: 0 },
  );

  const isLegacyAggregateRecord = Boolean(
    currentRecord.id &&
    !currentRecord.monthly_breakdown &&
    (Number(currentRecord.gross_salary) > 0 || Number(currentRecord.tds_deducted) > 0),
  );

  const handleSubmit = async () => {
    if (isLegacyAggregateRecord && totals.gross_salary === 0 && totals.tds_deducted === 0) {
      Swal.fire(
        "Month-wise values required",
        "Enter the prior income and TDS by month before replacing the existing aggregate version.",
        "warning",
      );
      return;
    }

    try {
      await api.post("/prior-employment", {
        ...form,
        user_id: employee.id,
        monthly_breakdown: form.monthly_breakdown.map((row) => ({
          month: row.month,
          gross_salary: Number(row.gross_salary || 0),
          tds_deducted: Number(row.tds_deducted || 0),
        })),
      });

      Swal.fire("Success", "Saved successfully", "success");
      await onSaved();
      onClose();
    } catch (error) {
      const validationErrors = error.response?.data?.errors;
      const firstValidationMessage = validationErrors
        ? Object.values(validationErrors).flat()[0]
        : null;
      Swal.fire(
        "Error",
        firstValidationMessage || error.response?.data?.message || "Failed to save prior employment",
        "error"
      );
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>

      <div className="modal-content prior-employment-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <span className="prior-modal-eyebrow">FY declaration</span>
            <h5>
              Prior Employment - {employee.first_name} {employee.last_name}
            </h5>
          </div>
          <button className="btn-close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="modal-body prior-employment-modal-body">
          <div className="prior-fy-summary">
            <div>
              <span>Financial Year</span>
              <strong>{form.financial_year}</strong>
            </div>
            <p>Enter only income and TDS received from employment before joining this company.</p>
          </div>

          {isLegacyAggregateRecord && (
            <div className="prior-legacy-notice">
              The existing FY record contains aggregate totals only: {formatPriorCurrency(currentRecord.gross_salary)} income and {formatPriorCurrency(currentRecord.tds_deducted)} TDS. Enter the month-wise values below to create the next version.
            </div>
          )}

          <div className="prior-month-grid prior-month-grid-header">
            <span>Month</span>
            <span>Gross income</span>
            <span>TDS deducted</span>
          </div>
          <div className="prior-month-list">
            {form.monthly_breakdown.map((row, index) => (
              <div className="prior-month-grid" key={row.month}>
                <strong>{row.month}</strong>
                <div className="prior-currency-input">
                  <span>₹</span>
                  <input
                    aria-label={`${row.month} gross income`}
                    type="number"
                    min="0"
                    step="0.01"
                    value={row.gross_salary}
                    onChange={(event) => updateMonth(index, "gross_salary", event.target.value)}
                  />
                </div>
                <div className="prior-currency-input">
                  <span>₹</span>
                  <input
                    aria-label={`${row.month} TDS deducted`}
                    type="number"
                    min="0"
                    step="0.01"
                    value={row.tds_deducted}
                    onChange={(event) => updateMonth(index, "tds_deducted", event.target.value)}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="prior-total-row">
            <strong>FY totals</strong>
            <span>{formatPriorCurrency(totals.gross_salary)}</span>
            <span>{formatPriorCurrency(totals.tds_deducted)}</span>
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn-submit" onClick={handleSubmit}>
            Save New Version
          </button>
        </div>



      </div>
    </div >
  );
}

function ArchiveModal({
  financialYears,
  selectedFinancialYear,
  onFinancialYearChange,
  selectedAsOfDate,
  onAsOfDateChange,
  employees,
  priorColumns,
  tentativeColumns,
  hraColumns,
  loading,
  onClose,
}) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content modal-large"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h5>Tax Declaration Archive</h5>
          <button className="btn-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="form-group mb-3">
            <label>Select Financial Year</label>
            <select
              className="form-control"
              value={selectedFinancialYear}
              onChange={(e) => onFinancialYearChange(e.target.value)}
            >
              {financialYears.map((financialYear) => (
                <option key={financialYear} value={financialYear}>
                  {financialYear}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group mb-3">
            <label>Archive view as of date</label>
            <input
              type="date"
              className="form-control"
              value={selectedAsOfDate}
              onChange={(e) => onAsOfDateChange(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading archive records...</p>
            </div>
          ) : (
            <>
              <div className="table-card mb-4">
                <div className="table-card-header">
                  <h6 className="table-card-title">Prior Employment Financials</h6>
                </div>
                <CommonTable
                  columns={priorColumns}
                  data={employees}
                  tableClassName="deductions-table"
                  wrapperClassName="table-responsive"
                  noDataClassName="text-center"
                  emptyMessage="No archive records found"
                  searchPlaceholder="Search employees..."
                  getSearchText={(employee) =>
                    [
                      employee.code,
                      employee.first_name,
                      employee.last_name,
                      employee.user_designation?.designation_detail?.designation_name,
                    ]
                      .filter(Boolean)
                      .join(" ")
                  }
                />
              </div>

              <div className="table-card mb-4">
                <div className="table-card-header">
                  <h6 className="table-card-title">Investments & Healthcare</h6>
                </div>
                <CommonTable
                  columns={tentativeColumns}
                  data={employees}
                  tableClassName="deductions-table"
                  wrapperClassName="table-responsive"
                  noDataClassName="text-center"
                  emptyMessage="No archive records found"
                  searchPlaceholder="Search employees..."
                  getSearchText={(employee) =>
                    [
                      employee.code,
                      employee.first_name,
                      employee.last_name,
                      employee.user_designation?.designation_detail?.designation_name,
                    ]
                      .filter(Boolean)
                      .join(" ")
                  }
                />
              </div>

              <div className="table-card">
                <div className="table-card-header">
                  <h6 className="table-card-title">Rent & Housing</h6>
                </div>
                <CommonTable
                  columns={hraColumns}
                  data={employees}
                  tableClassName="deductions-table"
                  wrapperClassName="table-responsive"
                  noDataClassName="text-center"
                  emptyMessage="No archive records found"
                  searchPlaceholder="Search employees..."
                  getSearchText={(employee) =>
                    [
                      employee.code,
                      employee.first_name,
                      employee.last_name,
                      employee.user_designation?.designation_detail?.designation_name,
                    ]
                      .filter(Boolean)
                      .join(" ")
                  }
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default DeductionsList;
